import React, { useState, useEffect } from 'react';
import { IPoliza, ICreatePoliza, IPolizaErrors, TipoPoliza } from '../types';
import FormHeader from '../components/FormHeader';
import SnackBar from '../components/SnackBar';
import Modal from '../components/Modal';
import { polizaService } from '../services/polizaService';
import { apiUtils } from '../config/api';
import { sociedadStorage } from '../stores/sociedadStorage';
import { polizaStorage } from '../stores/polizaStorage';
import { clearAllStorage } from '../utils/storageUtils';
import './poliza-form.css';

interface PolizaFormProps {
  sociedadId: number;
  onBack: () => void;
  onComplete: (poliza: IPoliza) => void;
  onError?: (error: string) => void;
  onNavigateToStart?: () => void;
}

/**
 * @fileoverview Componente de formulario para crear una póliza
 * @description Permite al usuario crear una póliza de seguros
 */

const PolizaForm: React.FC<PolizaFormProps> = ({
  sociedadId,
  onBack,
  onComplete,
  onError,
  onNavigateToStart
}) => {
  const [formData, setFormData] = useState<ICreatePoliza>({
    tipo: 'Responsabilidad Civil y Accidentes',
    formaPago: 'Domiciliación Bancaría',
    iban: '',
    comentarios: '',
    idSociedad: sociedadId,
  });

  const [errors, setErrors] = useState<IPolizaErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFormaster, setIsFormaster] = useState(false);

  /**
   * Obtiene el ID de la sociedad del LocalStorage al cargar el componente
   * y carga los datos guardados del formulario si existen
   */
  useEffect(() => {
    const storedSociedadId = sociedadStorage.getSociedadId();
    if (storedSociedadId && storedSociedadId !== sociedadId) {
      setFormData(prev => ({
        ...prev,
        idSociedad: storedSociedadId
      }));
    }

    // Cargar datos guardados del formulario si existen
    const storedPoliza = polizaStorage.getPoliza();

    if (storedPoliza) {
      setFormData(prev => ({
        ...storedPoliza,
        idSociedad: storedSociedadId || prev.idSociedad
      }));
    }

    // Comprobar si la sociedad es FORMASTER
    const storedSociedad = sociedadStorage.getSociedad();
    if (storedSociedad?.condicionesIdAsociacion === 'FMASTER') {
      setIsFormaster(true);
      setFormData(prev => ({
        ...prev,
        tipo: 'Responsabilidad Civil y Accidentes'
      }));
    }
  }, [sociedadId]);

  /**
   * Maneja cambios en los inputs y actualiza el estado del formulario
   * @param e - Evento de cambio del input
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    const updatedData = {
      ...formData,
      [name]: value
    };

    setFormData(updatedData);

    // Guardar en LocalStorage cuando cambian los datos
    polizaStorage.savePoliza(updatedData);

    // Clear error when user starts typing
    if (errors[name as keyof IPolizaErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Valida los datos del formulario
   * @returns boolean - true si la validación es exitosa
   */
  const validateForm = (): boolean => {
    const newErrors: IPolizaErrors = {};

    // Validación de tipo de póliza
    if (!formData.tipo || formData.tipo.trim() === '') {
      newErrors.tipo = 'El tipo de póliza es obligatorio';
    }

    // Validación de IBAN
    if (!formData.iban.trim()) {
      newErrors.iban = 'El IBAN es obligatorio';
    } else {
      const ibanRegex = /^ES\d{2}(?:\s?\d{4}){5}$/;
      if (!ibanRegex.test(formData.iban.trim())) {
        newErrors.iban = 'Formato de IBAN inválido - Ej: ES12 3456 7890 1234 5678 9012';
      }
    }

    // Validación de comentarios (opcional, pero si se proporciona debe tener al menos 3 caracteres)
    if (formData.comentarios && formData.comentarios.trim().length > 0 && formData.comentarios.trim().length < 3) {
      newErrors.comentarios = 'Los comentarios deben tener al menos 3 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el envío del formulario
   * @param e - Evento de envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // Preparar datos para la API
        const apiData = {
          tipo: formData.tipo,
          formaPago: formData.formaPago,
          iban: formData.iban.trim(),
          comentarios: formData.comentarios?.trim() || null,
          idSociedad: formData.idSociedad
        };

        // Llamada real a la API
        const response = await polizaService.crearPoliza(apiData);

        if (response.success) {
          setSuccessMessage(response.message || 'Póliza creada exitosamente');
          setShowSuccessSnackbar(true);

          // Crear objeto póliza con el ID devuelto por la API
          const poliza: IPoliza = {
            idPoliza: response.data?.idPoliza || Math.floor(Math.random() * 1000),
            ...formData
          };

          // Limpiar todo el localStorage después de completar exitosamente
          // Esto incluye el tipo de formación para permitir nuevas solicitudes
          clearAllStorage();
          // Asegurar que el tipo de formación se limpie explícitamente
          sociedadStorage.clearTipoFormacion();

          setTimeout(() => {
            onComplete(poliza);
          }, 2000);
        } else {
          setErrorMessage(response.message || 'Error al crear la póliza');
        }

      } catch (error) {
        const errorMsg = apiUtils.handleError(error);
        setErrorMessage(errorMsg);
        if (onError) {
          onError(errorMsg);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="container">
      <div className="form-container">
        <FormHeader onLogoClick={onNavigateToStart} />
        <h2 className="form-subtitle">PÓLIZA DE SEGUROS</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="tipo">
              Tipo de Póliza <span className="required">*</span>
            </label>
            <select
              id="tipo"
              name="tipo"
              value={formData.tipo}
              onChange={handleInputChange}
              className={errors.tipo ? 'error' : ''}
              required
              disabled={isFormaster}
              aria-describedby={errors.tipo ? 'tipo-error' : undefined}
            >
              <option value="Responsabilidad Civil y Accidentes">Responsabilidad Civil y Accidentes</option>
              {!isFormaster && (
                <>
                  <option value="Responsabilidad Civil">Responsabilidad Civil</option>
                  <option value="Accidentes">Accidentes</option>
                </>
              )}
            </select>
            {errors.tipo && <span id="tipo-error" className="error-message">{errors.tipo}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="formaPago">
              Forma de Pago
            </label>
            <input
              type="text"
              id="formaPago"
              name="formaPago"
              value={formData.formaPago}
              disabled
              className="disabled-field"
            />
          </div>

          <div className="form-group">
            <label htmlFor="iban">
              IBAN <span className="required">*</span>
            </label>
            <input
              type="text"
              id="iban"
              name="iban"
              value={formData.iban}
              onChange={handleInputChange}
              className={errors.iban ? 'error' : ''}
              required
              placeholder="ES12 3456 7890 1234 5678 9012"
              maxLength={29}
              aria-describedby={errors.iban ? 'iban-error' : 'iban-help'}
            />
            {errors.iban && <span id="iban-error" className="error-message">{errors.iban}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="comentarios">
              Comentarios
            </label>
            <textarea
              id="comentarios"
              name="comentarios"
              value={formData.comentarios || ''}
              onChange={handleInputChange}
              className={errors.comentarios ? 'error' : ''}
              rows={4}
              placeholder="Comentarios adicionales sobre la póliza (opcional)"
              aria-describedby={errors.comentarios ? 'comentarios-error' : undefined}
            />
            {errors.comentarios && <span id="comentarios-error" className="error-message">{errors.comentarios}</span>}
          </div>

          <div className="button-group">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onNavigateToStart}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              Atrás
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Solicitando...' : 'Solicitar Póliza'}
            </button>
          </div>
        </form>

        {/* SnackBar para éxito */}
        <SnackBar
          isOpen={showSuccessSnackbar}
          onClose={() => setShowSuccessSnackbar(false)}
          message={successMessage}
          type="success"
          duration={4000}
          position="bottom-center"
        />

        {/* Modal para errores */}
        <Modal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title="Error al crear póliza"
        >
          <div style={{ color: '#d32f2f', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 16px 0' }}>
              <strong>Error:</strong> {errorMessage}
            </p>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
              Por favor, revise los datos e intente nuevamente.
            </p>
            <div style={{ textAlign: 'right' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowErrorModal(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default PolizaForm;
